import { memo, MouseEventHandler, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  graphql,
  useFragment,
  useRelayEnvironment,
  useSubscription,
} from "react-relay";
import LogoIcon from "./LogoIcon";
import { assertNever } from "@/utils/helpers";
import { Copy04Icon } from "@/icons";
import { Button } from "@/kit/ui/button";
import { fetchDatasetVersionCitation } from "@/utils/dataset";
import { toast } from "@/utils/toast";
import { DatasetDOIFragment$key } from "./__generated__/DatasetDOIFragment.graphql";
import {
  DatasetDOISubscription,
  DatasetVersionStatus,
} from "./__generated__/DatasetDOISubscription.graphql";
import { logger } from "@/common/logger";
import {
  Tooltip,
  TooltipPortal,
  TooltipContent,
  TooltipTrigger,
} from "@/kit/ui/tooltip";
import { useClipboardAvailable } from "@/utils/hooks";

const Fragment = graphql`
  fragment DatasetDOIFragment on DatasetVersion {
    id
    doi {
      full
      short
      url
    }
  }
`;

export interface DatasetDOIProps {
  datasetVersion: DatasetDOIFragment$key;
}

export default function DatasetDOI({ ...props }: DatasetDOIProps) {
  const relay = useRelayEnvironment();
  const datasetVersion = useFragment(Fragment, props.datasetVersion);
  const clipboard = useClipboardAvailable();

  if (!datasetVersion.doi) return null;
  const { full, short, url } = datasetVersion.doi;

  const onCopy: MouseEventHandler = (evt) => {
    evt.preventDefault();

    toast.promise(fetchDatasetVersionCitation(relay, datasetVersion.id), {
      loading: (
        <FormattedMessage defaultMessage="BibTeX citation currently generating..." />
      ),
      success(citation) {
        navigator.clipboard.writeText(citation);
        return <FormattedMessage defaultMessage="BibTeX copied to clipboard" />;
      },
      error(error) {
        logger.error(error);
        return (
          <FormattedMessage defaultMessage="Failed to generate a BibTeX citation, please report this bug!" />
        );
      },
    });
  };

  return (
    <Tooltip delayDuration={0}>
      <div className="self-stretch flex gap-2">
        <a
          href={url}
          target="_blank"
          className="flex-1 inline-flex border border-grey-200 rounded-md overflow-clip"
        >
          <span className="flex items-center gap-2 py-1.5 px-3 bg-linear-to-b from-white to-[#EFEDFC]">
            <LogoIcon className="w-4 h-4" />
            <span className="font-medium text-sm text-brand-600">
              <FormattedMessage defaultMessage="DOI" />
            </span>
          </span>

          <span className="flex-1 text-center content-center font-medium text-sm text-grey-900">
            {short ?? full}
          </span>
        </a>

        {clipboard && (
          <TooltipTrigger asChild>
            <Button
              onClick={onCopy}
              size="md"
              variant="secondary"
              className="p-2"
            >
              <Copy04Icon className="h-5 w-5 text-grey-700" />
            </Button>
          </TooltipTrigger>
        )}
      </div>
      <TooltipPortal>
        <TooltipContent>
          <FormattedMessage defaultMessage="Copy BibTeX" />
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
}

const Subscription = graphql`
  subscription DatasetDOISubscription($datasetVersionId: ID!) {
    datasetVersionUpdate(datasetVersionId: $datasetVersionId) {
      status
      datasetVersion {
        ...DatasetDOIFragment
      }
    }
  }
`;

export interface PendingDatasetDOIProps {
  datasetVersion: DatasetDOIFragment$key;
}

// Memo is needed to correctly pull and react to events sent by server.
const PendingDatasetDOI = memo(function PendingDatasetDOI(
  props: PendingDatasetDOIProps,
) {
  const { id } = useFragment(Fragment, props.datasetVersion);

  const [state, setState] = useState<DatasetVersionStatus>("GENERATING_DOI");

  useSubscription<DatasetDOISubscription>({
    subscription: Subscription,
    variables: { datasetVersionId: id },
    onNext(next) {
      setState(next?.datasetVersionUpdate?.status ?? "IDLE");
    },
  });

  switch (state) {
    case "IDLE":
      return null;

    case "GENERATING_DOI":
      return <GeneratingDOI />;

    default:
      assertNever(state);
  }
});

const GeneratingDOI = () => (
  <div className="w-full font-medium text-center">
    <span className="font-medium text-transparent bg-clip-text bg-linear-to-r from-[#667085] to-[#E2E6ED] animate-pulse">
      <FormattedMessage defaultMessage="Generating DOI..." />
    </span>
  </div>
);

DatasetDOI.Pending = PendingDatasetDOI;
